<template>
  <div class="container is-max-hd">
    <!-- PATIENT HISTORY TABLE -->
    <div class="box history-table-container">
      <div class="section-header-collapsible mobile-only" @click="isHistoryOpen = !isHistoryOpen">
        <h5 class="title is-5">Patient History</h5>
        <b-icon :icon="isHistoryOpen ? 'chevron-up' : 'chevron-down'"></b-icon>
      </div>
      <b-collapse v-model="isHistoryOpen" animation="slide" class="history-collapse">
        <div class="section">
          <div class="history-table">
            <b-table
              :data="history"
              :loading="patientHistoryLoading"
              :row-class="getInquiryRowClass"
              hoverable
              class="is-fullwidth"
              ref="historyTable"
            >
              <!-- PATIENT COLUMN -->
              <b-table-column field="patient" label="Patient" v-slot="props">
                <a
                  v-if="props.row.status === 'complete' || props.row.status === 'expired'"
                  class="history-link"
                  @click="viewInquiryDetails(props.row)"
                >
                  <div class="is-size-6">{{ formatPatientName(props.row) }}</div>
                  <div class="has-text-grey is-size-7">{{ props.row.date }}</div>
                </a>
                <div v-else class="history-text-disabled">
                  <div class="is-size-6 has-text-grey-light">
                    {{ formatPatientName(props.row) }}
                  </div>
                  <div class="has-text-grey is-size-7">{{ props.row.date }}</div>
                </div>
              </b-table-column>

              <!-- STATUS COLUMN -->
              <b-table-column field="status" label="Status" v-slot="props">
                <span
                  :class="{
                    tag: true,
                    'is-success': props.row.plan_status == 'active',
                    'is-danger': props.row.plan_status == 'inactive',
                    'is-light': props.row.plan_status == 'draft',
                    'is-warning': props.row.plan_status == 'unavailable',
                  }"
                >
                  {{ props.row.plan_status }}
                </span>
              </b-table-column>

              <!-- ACTIONS COLUMN -->
              <b-table-column v-if="isManager" field="actions" label="Actions" v-slot="props">
                <div class="is-flex is-align-items-center is-justify-content-center">
                  <span class="is-clickable" @click="handleDeleteInquiry(props.row, selectedID)">
                    <b-icon icon="delete" type="is-danger"></b-icon>
                  </span>
                </div>
              </b-table-column>

              <!-- EMPTY STATE -->
              <template #empty>
                <div v-if="!patientHistoryLoading" class="has-text-centered has-text-grey">
                  <b-icon icon="information-outline" size="is-medium" class="mb-2"></b-icon>
                  <p class="is-size-6">No previous inquiries exist for this patient.</p>
                </div>
              </template>
            </b-table>
          </div>
        </div>
      </b-collapse>
    </div>

    <!-- EXISTING FILTERS -->
    <div class="box filter-container">
      <div class="section-header-collapsible mobile-only" @click="isFiltersOpen = !isFiltersOpen">
        <h5 class="title is-5">Filters</h5>
        <b-icon :icon="isFiltersOpen ? 'chevron-up' : 'chevron-down'"></b-icon>
      </div>
      <b-collapse v-model="isFiltersOpen" animation="slide" class="filters-collapse">
        <div class="collapse-content">
          <h5 class="title is-5">Networks</h5>
          <b-field v-for="item in availableNetworks()" :key="'network-' + item.key">
            <b-checkbox
              v-model="networkContent"
              @input="handleNetworkChange(networkContent)"
              :native-value="item.key"
              :disabled="!item.enabled"
            >
              <span>{{ item.label }}</span>
            </b-checkbox>
          </b-field>

          <hr />

          <h5 class="title is-5">Details</h5>
          <b-field v-for="item in availablePayments()" :key="'payment-' + item.key">
            <b-checkbox
              v-model="paymentContent"
              @input="handlePaymentChange(paymentContent)"
              :native-value="item.key"
              :disabled="!item.enabled"
            >
              <span>{{ item.label }}</span>
            </b-checkbox>
          </b-field>

          <hr />

          <h5 class="title is-5">Benefits</h5>
          <b-field v-for="item in availableBenefits()" :key="'benefit-' + item.key">
            <b-checkbox
              v-model="benefitContent"
              @input="handleBenefitsChange(benefitContent)"
              :native-value="item.key"
            >
              <span>{{ item.label }}</span>
            </b-checkbox>
          </b-field>

          <hr />

          <h5 class="title is-5">Levels</h5>
          <b-field v-for="item in availableLevels" :key="'level-' + item">
            <b-radio
              v-model="levelContent"
              @input="$emit('input-level', levelContent)"
              :native-value="item"
            >
              <span>{{ item }}</span>
            </b-radio>
          </b-field>
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import request from '../request';
import { possiblePayments, possibleNetworks } from '../constants.js';
import { mapGetters } from 'vuex';

export default {
  name: 'InquiryDetailSideBar',
  props: {
    plan: Object,
    selectedNetworks: Array,
    selectedPayments: Array,
    selectedBenefits: Array,
    selectedLevel: String,
    patientHistory: {
      type: Array,
      default: () => [],
    },
    patientHistoryLoading: {
      type: Boolean,
      default: false,
    },
    selectedId: String,
  },
  data() {
    const isMobile = window.innerWidth <= 1024;
    return {
      networkContent: this.selectedNetworks,
      paymentContent: this.selectedPayments,
      benefitContent: this.selectedBenefits,
      levelContent: this.selectedLevel,
      availableLevels: ['All', 'Family', 'Individual'],
      isHistoryOpen: !isMobile, // Closed on mobile/tablet, open on desktop
      isFiltersOpen: !isMobile, // Closed on mobile/tablet, open on desktop
      deletingInquiry: false,
    };
  },
  computed: {
    ...mapGetters(['isManager']),
    history() {
      return this.patientHistory || [];
    },
    selectedID() {
      return this.selectedId;
    },
  },
  watch: {
    selectedId(newId) {
      // Scroll to selected inquiry when it changes
      if (newId) {
        this.$nextTick(() => {
          this.scrollToSelectedInquiry();
        });
      }
    },
    patientHistory() {
      // Also scroll when patient history data loads
      this.$nextTick(() => {
        this.scrollToSelectedInquiry();
      });
    },
  },
  mounted() {
    // Scroll to selected inquiry on initial load
    this.$nextTick(() => {
      this.scrollToSelectedInquiry();
    });
  },
  methods: {
    // Return the networks types that are in use.
    availableNetworks() {
      const networks = possibleNetworks.map((n) => ({
        key: n.key,
        label: n.label,
        enabled: this.plan.benefit_groups.some(
          (group) =>
            [...(group.benefits || []), ...(group.auxilary || [])].some((benefit) => benefit.network_code === n.key)
        ),
      }));

      networks.unshift({ key: 'All', label: 'All', enabled: true });

      return networks;
    },

    // Return the payment types types that are in use.
    availablePayments() {
      const payments = possiblePayments.map((p) => ({
        key: p.key,
        label: p.label,
        enabled: this.plan.benefit_groups.some(
          (group) =>
            [...(group.benefits || []), ...(group.auxilary || [])].some((benefit) => benefit.amount_code === p.key)
        ),
      }));

      payments.unshift({ key: 'All', label: 'All', enabled: true });

      return payments;
    },

    // Return the benefit types types that are in use.
    // Benefit types are unknown so we don't iterate over
    // a known set like with networks and payments.
    availableBenefits() {
      const benefits = this.plan.benefit_groups
        .filter((group) => group.name)
        .map((group, i) => ({
          key: group.name,
          label: group.name,
          order: group.name?.toLowerCase() === 'health benefit plan coverage' ? 0 : i + 1, // ensure health benefit plan coverage is first
        }));

      // Reorder benefits
      benefits.sort((a, b) => a.order - b.order);

      benefits.unshift({ key: 'All', label: 'All' });

      return benefits;
    },
    handleNetworkChange(newValue) {
      if (this.allIsFirstItem(newValue)) {
        this.networkContent = this.networkContent.filter((i) => i != 'All');
      } else if (this.allIsLastItem(newValue)) {
        this.networkContent = this.networkContent.filter((i) => i == 'All');
      }

      this.$emit('input-network', this.networkContent);
    },
    handlePaymentChange(newValue) {
      if (this.allIsFirstItem(newValue)) {
        this.paymentContent = this.paymentContent.filter((i) => i != 'All');
      } else if (this.allIsLastItem(newValue)) {
        this.paymentContent = this.paymentContent.filter((i) => i == 'All');
      }

      this.$emit('input-payment', this.paymentContent);
    },
    handleBenefitsChange(newValue) {
      if (this.allIsFirstItem(newValue)) {
        this.benefitContent = this.benefitContent.filter((i) => i != 'All');
      } else if (this.allIsLastItem(newValue)) {
        this.benefitContent = this.benefitContent.filter((i) => i == 'All');
      }

      this.$emit('input-benefit', this.benefitContent);
    },
    allIsFirstItem(newValue) {
      return newValue.length > 1 && newValue[0] == 'All';
    },
    allIsLastItem(newValue) {
      return newValue.length > 1 && newValue[newValue.length - 1] == 'All';
    },
    viewInquiryDetails(inquiry) {
      // Only allow viewing complete or expired inquiries
      if (inquiry.status === 'complete' || inquiry.status === 'expired') {
        this.$emit('select-inquiry', inquiry.id);
      }
    },
    formatPatientName(inquiry) {
      if (!inquiry.patient) return 'Unknown Patient';

      // Split the patient name (e.g., "SAMAN JOZANI" -> ["SAMAN", "JOZANI"])
      const nameParts = inquiry.patient.trim().split(/\s+/);

      if (nameParts.length === 0) return 'Unknown Patient';
      if (nameParts.length === 1) return nameParts[0];

      // First name + last initial
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      const lastInitial = lastName.charAt(0).toUpperCase() + '.';

      return `${firstName} ${lastInitial}`;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    },
    handleDeleteInquiry(inquiry, selectedID) {
      if (!inquiry) return;

      if (inquiry.id === selectedID) {
        this.$buefy.dialog.confirm({
          title: 'Remove inquiry',
          message:
            "You can't delete the inquiry currently selected, please select a different one.",
          type: 'is-danger',
        });
        return;
      }

      let message = `Are you sure you want to remove this inquiry?`;
      message += `<br/><br/>NOTE: This will not delete the related data, but this inquiry will no longer be visible to you or your organization's users.`;

      this.$buefy.dialog.confirm({
        title: 'Remove inquiry',
        message: message,
        confirmText: `Remove inquiry`,
        type: 'is-danger',
        onConfirm: () => {
          this.deletingInquiry = true;

          const loadingComponent = this.$buefy.loading.open({
            container: this.$el,
          });

          request
            .put(`/inquiries`, { inquiry_ids: [inquiry.id], is_hidden: true })
            .then(() => {
              this.$buefy.toast.open({
                message: `Successfully removed inquiry`,
                type: 'is-success',
                duration: 10000,
              });
            })
            .catch((error) => {
              this.$buefy.toast.open({
                message: error.response?.data?.error || 'Failed to remove inquiry',
                type: 'is-danger',
                duration: 5000,
              });
            })
            .finally(() => {
              this.deletingInquiry = false;
              loadingComponent.close();
              this.$emit('reload-inquiries');
            });
        },
      });
    },
    getInquiryRowClass(inq) {
      return inq.id === this.selectedID ? 'is-selected' : '';
    },
    scrollToSelectedInquiry() {
      if (!this.selectedID || !this.history.length) return;

      // Find the selected inquiry element
      const historyTable = this.$el.querySelector('.history-table');
      if (!historyTable) return;

      const selectedRow = historyTable.querySelector('.is-selected');
      if (!selectedRow) return;

      // Scroll the selected row into view with smooth behavior
      selectedRow.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    },
  },
};
</script>

<style scoped>
/* Collapsible Section Header - Mobile Only */
.section-header-collapsible {
  display: none;
}

@media screen and (max-width: 1024px) {
  .section-header-collapsible {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    cursor: pointer;
    user-select: none;
    border-bottom: 1px solid #f0f0f0;
  }

  .section-header-collapsible:hover {
    background-color: #fafafa;
  }

  .section-header-collapsible .title {
    margin: 0;
    color: #2c3e50;
    font-size: 1.1rem;
    font-weight: 600;
  }
}

/* Patient History Table Container */
.history-table-container {
  margin-bottom: 0.75rem;
  padding: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-top: 4px solid #7956d4;
}

.history-table-container .section {
  margin-bottom: 0.25rem;
  padding: 0.5rem;
}

/* Desktop: Restore original padding */
@media screen and (min-width: 1025px) {
  .history-table-container {
    padding: 0.5rem;
  }
}

/* Scrollable Table */
.history-table {
  margin-bottom: 0;
  height: 210px;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.history-table :deep(.table) {
  margin-bottom: 0;
  border: none;
  width: 100%;
}

.history-table :deep(.table-wrapper) {
  width: 100%;
}

.history-table :deep(.table td) {
  padding: 0.35rem 0.5rem;
  border: none;
  width: auto;
}

.history-table :deep(.table th) {
  padding: 0.35rem 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  border: none;
  background: #f8f9fa;
  font-size: 1.1rem;
  width: auto;
  position: sticky;
  top: 0;
  z-index: 10;
}

/* Column Widths - Exact match to blanket VOB */
.history-table :deep(.table th:first-child),
.history-table :deep(.table td:first-child) {
  width: 50%;
}

.history-table :deep(.table th:nth-child(2)),
.history-table :deep(.table td:nth-child(2)) {
  width: 30%;
}

.history-table :deep(.table th:last-child),
.history-table :deep(.table td:last-child) {
  width: 20%;
  text-align: center;
}

/* Row Hover and Selection - Enhanced visibility */
.history-table :deep(.table tr:hover) {
  background-color: #f5f5f5;
}

.history-table :deep(.table tr.is-selected) {
  background-color: #e8f4fd;
  border-left: 3px solid #7956d4;
  font-weight: 600;
}

.history-table :deep(.table tr.is-selected td) {
  color: #2c3e50;
}

.history-table :deep(.table tr.is-selected .history-link) {
  color: #7956d4;
  font-weight: 600;
}

/* Tags - Exact match to blanket VOB */
.history-table :deep(.tag) {
  font-size: 0.8rem;
  padding: 0.2em 0.5em;
}

.history-table :deep(.icon) {
  font-size: 1rem;
}

/* Patient Link */
.history-link {
  color: #3273dc;
  cursor: pointer;
  text-decoration: none;
}

.history-link:hover {
  text-decoration: underline;
}

/* Disabled/Non-clickable Items - Subtle styling */
.history-text-disabled {
  cursor: default;
}

.history-text-disabled .is-size-6 {
  color: #7a7a7a;
}

/* Filter Container */
.filter-container {
  border-radius: 12px;
  padding: 0;
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.filter-container .collapse-content {
  padding: 1rem 1.5rem 1.5rem 1.5rem;
}

/* Mobile/Tablet: Match all containers to button width */
@media screen and (max-width: 1024px) {
  /* Remove container padding to allow manual control */
  .container.is-max-hd {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  /* Add consistent horizontal margins to all sidebar containers */
  .history-table-container,
  .filter-container {
    margin-left: 1rem;
    margin-right: 1rem;
    width: calc(100% - 2rem);
  }

  .filter-container {
    padding: 0;
  }
}

.filter-container .collapse-content .title.is-5 {
  padding: 0;
  margin-bottom: 1rem;
}

.filter-container hr {
  background: #ddd;
}

/* Desktop: Always show collapse content */
@media screen and (min-width: 1025px) {
  .history-collapse,
  .filters-collapse {
    display: block !important;
    height: auto !important;
    overflow: visible !important;
  }

  .filter-container {
    padding: 1.5rem;
  }

  .filter-container .collapse-content {
    padding: 0;
  }

  .filter-container .collapse-content .title.is-5 {
    padding: 0;
    padding-top: 0;
    margin-top: 0;
  }

  .filter-container .collapse-content .title.is-5:first-child {
    margin-top: 0;
  }
}
</style>
